<template>
  <div>
    <v-row
      v-if="canSaveTraining || canSubmitTraining"
      justify="end"
      class="mb-3"
    >
      <v-col cols="auto">
        <template v-if="canSaveTraining">
          <template v-if="isTrainingEditing">
            <v-btn
              v-if="training._id"
              depressed
              color="error"
              :min-width="minWidth"
              class="rounded-1 ms-4"
              :disabled="isTrainingSaving"
              @click="onCancel"
            >
              {{
                isSaveButtonDisabled
                  ? $t("course.courseDetails.close")
                  : $t("course.courseDetails.cancel")
              }}
            </v-btn>

            <v-btn
              depressed
              color="success"
              :min-width="minWidth"
              class="rounded-1 ms-4"
              :loading="isTrainingSaving"
              :disabled="isSaveButtonDisabled"
              @click="saveTraining"
            >
              {{ $t("course.courseDetails.save") }}
            </v-btn>
          </template>

          <v-btn
            v-else
            depressed
            color="primary"
            :min-width="minWidth"
            class="rounded-1 ms-4"
            @click="isTrainingEditing = true"
          >
            {{ $t("course.courseDetails.edit") }}
          </v-btn>
        </template>

        <v-btn
          v-if="canSubmitTraining"
          depressed
          color="primary"
          :min-width="minWidth"
          class="rounded-1 ms-4"
          :loading="isTrainingSubmitting"
          @click="submitTrainingForApproval"
        >
          {{ $t("course.courseDetails.submit") }}
        </v-btn>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { createOrUpdateTraining } from "@/app/course-module/methods/createOrUpdateTraining";
import { submitTrainingForApproval } from "@/app/course-module/methods/submitTrainingForApproval";
import { hasPermission } from "@/app/user-module/helpers/authHelper";

export default {
  name: "CourseSaveAndSubmit",
  props: {
    training: {
      type: Object,
      required: true,
    },
    isSaving: {
      type: Boolean,
      required: true,
    },
    isEditing: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      isTrainingSubmitting: false,
    };
  },
  computed: {
    minWidth() {
      return this.$vuetify.breakpoint.smAndDown ? "90" : "120";
    },
    isSaveButtonDisabled() {
      const hasInvalidData =
        this.training.hasInvalidTitle ||
        !this.training.title?.find(x => x.value?.trim()?.length) ||
        this.training.feedback?.questionAnswers?.some(
          fq => fq.hasInvalidQuestion || fq.hasInvalidChoices
        ) ||
        this.training.trainingModules?.some(
          tm =>
            tm.hasInvalidTitle ||
            tm.trainingModuleContents?.some(
              tmc =>
                tmc.hasInvalidTitle ||
                (tmc.contentType === "QUIZ" &&
                  (!tmc.quizContent ||
                    tmc.quiz?.questionAnswers?.some(
                      tmcq => tmcq.hasInvalidQuestion || tmcq.hasInvalidChoices
                    )))
            )
        );

      const hasNoChanges = !(
        this.training.hasChangesInTrainingCategories ||
        this.training.hasChangesInTrainingSubCategories ||
        this.training.hasChangesInTitle ||
        this.training.hasChangesInDescription ||
        this.training.hasChangesInInstructions ||
        this.training.hasChangesInFeedback ||
        this.training.hasChangesInImages ||
        this.training.hasChangesInDuration ||
        this.training.hasChangesInCertificateValidity ||
        this.training.hasChangesInPrice ||
        this.training.trainingModules?.some(
          tm =>
            tm.hasChangesInTitle ||
            tm.hasChangesInDescription ||
            tm.hasChangesInDisplayPosition || //TODO: hasChangesInDisplayPosition while drag
            tm.trainingModuleContents?.some(
              tmc =>
                tmc.hasChangesInTrainingModule || //TODO: hasChangesInTrainingModule while drag
                tmc.hasChangesInQuizContent ||
                tmc.hasChangesInContentType ||
                tmc.hasChangesInTitle ||
                tmc.hasChangesInDescription ||
                tmc.hasChangesInVideos ||
                tmc.hasChangesInAudios ||
                tmc.hasChangesInText ||
                tmc.hasChangesInQuiz ||
                tmc.hasChangesInDuration ||
                tmc.hasChangesInDisplayPosition //TODO: hasChangesInDisplayPosition
            )
        ) ||
        this.training.deletedTrainingModules?.length ||
        this.training.deletedTrainingModuleContents?.length
      );

      return hasInvalidData || hasNoChanges;
    },
    canSaveTraining() {
      return (
        this.training._id == null ||
        this.$store.state.authenticatedUserRole === "ADMIN" ||
        (this.$store.state.authenticatedUserRole === "EMPLOYEE" &&
          this.havePermissionUpdateTraining)
        // && this.$store.state.authenticatedUser?._id ===
        //   this.training.createdBy?._id)
      );
    },
    canSubmitTraining() {
      return (
        this.training._id != null &&
        this.$store.state.authenticatedUser?._id ===
          this.training.createdBy?._id &&
        (this.training.approvalStatus == null ||
          this.training.approvalStatus === "PREPARING") &&
        !this.isTrainingEditing
      );
    },
    isTrainingSaving: {
      get() {
        return this.isSaving;
      },
      set(value) {
        this.$emit("update:isSaving", value);
      },
    },
    isTrainingEditing: {
      get() {
        return this.isEditing;
      },
      set(value) {
        this.$emit("update:isEditing", value);
      },
    },
    havePermissionUpdateTraining() {
      return hasPermission({
        currentRole: this.$store.state.authenticatedUserRole,
        currentPermissions: this.$store.state.authenticatedUserPermissions,
        requiredPermission: ["UPDATE_TRAINING"],
        requiredAll: true,
        restrictOrganizationManager:
          this.$store.state.authenticatedUser?.isOrganizationManager,
      });
    },
  },
  methods: {
    saveTraining() {
      if (this.isTrainingSaving) return;
      this.isTrainingSaving = true;

      createOrUpdateTraining(this.training).then(response => {
        if (response.data) {
          this.$emit("onSuccess");

          if (!this.training?._id) {
            this.$router.replace({
              name: "CourseDetails",
              params: { trainingId: response.data._id },
            });
          }
        } else if (response.error) this.$emit("onError");

        this.isTrainingSaving = false;
      });
    },
    submitTrainingForApproval() {
      if (this.isTrainingSubmitting) return;
      this.isTrainingSubmitting = true;

      submitTrainingForApproval({
        id: this.training?._id,
      }).then(response => {
        if (response.data) this.$emit("onSuccess");
        else if (response.error) this.$emit("onError");

        this.isTrainingSubmitting = false;
      });
    },
    onCancel() {
      this.isTrainingEditing = false;
      this.$emit("onCancel");
    },
  },
};
</script>

<style lang="scss" scoped />
